import React, { useMemo, useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import {
  registerCustomerWithEmail,
  requestCustomerPasswordReset,
  signInCustomerWithEmail,
  signInCustomerWithGoogle,
  signOutCustomer,
} from '../utils/customerAuth';

const mapAuthError = (error) => {
  switch (error?.code) {
    case 'auth/invalid-email':
      return 'Az e-mail cím formátuma nem megfelelő.';
    case 'auth/invalid-credential':
    case 'auth/user-not-found':
    case 'auth/wrong-password':
      return 'Hibás e-mail cím vagy jelszó.';
    case 'auth/email-already-in-use':
      return 'Ez az e-mail cím már használatban van.';
    case 'auth/weak-password':
      return 'A jelszó túl gyenge.';
    case 'auth/network-request-failed':
      return 'Hálózati hiba történt. Próbáld újra.';
    case 'auth/too-many-requests':
      return 'Túl sok próbálkozás történt. Kérlek várj pár percet.';
    default:
      return 'A bejelentkezés sikertelen. Ellenőrizd a Firebase Auth beállításokat.';
  }
};

const mapPasswordResetError = (error) => {
  switch (error?.code) {
    case 'auth/invalid-email':
      return 'Az e-mail cím formátuma nem megfelelő.';
    case 'auth/network-request-failed':
      return 'Hálózati hiba történt. Próbáld újra.';
    case 'auth/too-many-requests':
      return 'Túl sok próbálkozás történt. Kérlek várj pár percet.';
    default:
      return 'A jelszó-visszaállító e-mail küldése nem sikerült. Próbáld újra később.';
  }
};

export default function Login() {
  const { user, loading } = useCustomerAuth();
  const [mode, setMode] = useState('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const modeLabel = useMemo(
    () => {
      if (mode === 'login') return 'Bejelentkezés';
      if (mode === 'register') return 'Regisztráció';
      return 'Visszaállító e-mail küldése';
    },
    [mode],
  );

  const resetMessages = () => {
    setError('');
    setSuccess('');
  };

  const handleGoogleSignIn = async () => {
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      await signInCustomerWithGoogle();
    } catch (err) {
      setError(mapAuthError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEmailSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!email.trim()) {
      setError('Az e-mail cím megadása kötelező.');
      return;
    }

    if (mode !== 'reset' && !password) {
      setError('Az e-mail cím és jelszó megadása kötelező.');
      return;
    }

    if (mode === 'register' && password !== confirmPassword) {
      setError('A két jelszó nem egyezik.');
      return;
    }

    setSubmitting(true);
    try {
      if (mode === 'reset') {
        await requestCustomerPasswordReset(email.trim());
        setSuccess(
          'Ha az e-mail címhez tartozik fiók, elküldtük a jelszó-visszaállító hivatkozást. Ellenőrizd a spam mappát is.',
        );
      } else if (mode === 'login') {
        await signInCustomerWithEmail(email.trim(), password);
      } else {
        await registerCustomerWithEmail(email.trim(), password, name.trim());
      }
    } catch (err) {
      if (mode === 'reset' && err?.code === 'auth/user-not-found') {
        setSuccess(
          'Ha az e-mail címhez tartozik fiók, elküldtük a jelszó-visszaállító hivatkozást. Ellenőrizd a spam mappát is.',
        );
      } else {
        setError(mode === 'reset' ? mapPasswordResetError(err) : mapAuthError(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignOut = async () => {
    setSubmitting(true);
    setError('');
    try {
      await signOutCustomer();
    } catch {
      setError('Nem sikerült kijelentkezni.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="section-padding">
        <div className="container-zinga max-w-2xl">
          <h1 className="font-display text-3xl md:text-4xl font-bold mb-6">
            Felhasználói Bejelentkezés
          </h1>

          {loading ? (
            <div className="rounded-3xl border border-border/70 bg-card p-8">
              <p className="text-sm text-muted-foreground">Bejelentkezési állapot ellenőrzése...</p>
            </div>
          ) : user ? (
            <div className="rounded-3xl border border-border/70 bg-card p-8 space-y-4">
              <p className="text-sm text-muted-foreground">Sikeresen be vagy jelentkezve.</p>
              <p className="text-sm font-medium">{user.displayName || user.email || user.uid}</p>
              <button
                type="button"
                onClick={handleSignOut}
                disabled={submitting}
                className="btn-secondary text-sm"
              >
                {submitting ? 'Kijelentkezés...' : 'Kijelentkezés'}
              </button>
            </div>
          ) : (
            <div className="rounded-3xl border border-border/70 bg-card p-8">
              <div className="mb-5 inline-flex rounded-xl border border-border overflow-hidden">
                <button
                  type="button"
                  onClick={() => {
                    setMode('login');
                    resetMessages();
                  }}
                  className={`px-4 py-2 text-sm ${
                    mode === 'login' ? 'bg-primary text-primary-foreground' : 'bg-card'
                  }`}
                >
                  Bejelentkezés
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMode('register');
                    resetMessages();
                  }}
                  className={`px-4 py-2 text-sm ${
                    mode === 'register' ? 'bg-primary text-primary-foreground' : 'bg-card'
                  }`}
                >
                  Regisztráció
                </button>
              </div>

              {mode === 'reset' ? (
                <p className="mb-4 text-sm text-muted-foreground">
                  Add meg a fiókodhoz tartozó e-mail címet, és küldünk egy hivatkozást az új
                  jelszó beállításához.
                </p>
              ) : null}

              <form onSubmit={handleEmailSubmit} className="grid gap-4">
                {mode === 'register' ? (
                  <div>
                    <label className="block text-xs font-medium mb-1">Név (opcionális)</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                      className="input-zinga"
                    />
                  </div>
                ) : null}
                <div>
                  <label className="block text-xs font-medium mb-1">E-mail cím</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    className="input-zinga"
                    autoComplete="email"
                  />
                </div>
                {mode !== 'reset' ? (
                  <div>
                    <label className="block text-xs font-medium mb-1">Jelszó</label>
                    <input
                      type="password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      className="input-zinga"
                      autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                    />
                    {mode === 'login' ? (
                      <button
                        type="button"
                        onClick={() => {
                          setMode('reset');
                          resetMessages();
                        }}
                        className="mt-2 text-xs font-medium text-primary hover:underline"
                      >
                        Elfelejtetted a jelszavad?
                      </button>
                    ) : null}
                  </div>
                ) : null}
                {mode === 'register' ? (
                  <div>
                    <label className="block text-xs font-medium mb-1">Jelszó megerősítése</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(event) => setConfirmPassword(event.target.value)}
                      className="input-zinga"
                      autoComplete="new-password"
                    />
                  </div>
                ) : null}
                <button type="submit" disabled={submitting} className="btn-primary text-sm">
                  {submitting ? 'Feldolgozás...' : modeLabel}
                </button>
                {mode === 'reset' ? (
                  <button
                    type="button"
                    onClick={() => {
                      setMode('login');
                      resetMessages();
                    }}
                    className="btn-secondary text-sm"
                  >
                    Vissza a bejelentkezéshez
                  </button>
                ) : null}
              </form>

              {mode !== 'reset' ? (
                <>
                  <div className="my-4 h-px bg-border/70" />

                  <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    disabled={submitting}
                    className="btn-secondary text-sm w-full"
                  >
                    Bejelentkezés Google-lel
                  </button>
                </>
              ) : null}

              {error ? (
                <p className="mt-4 rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                  {error}
                </p>
              ) : null}
              {success ? (
                <p
                  className="mt-4 rounded-xl border border-primary/30 bg-primary/10 p-3 text-sm text-foreground"
                  role="status"
                >
                  {success}
                </p>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
